import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { Plus, X, UploadCloud, Image as ImageIcon, Trash2, Loader2, CameraOff, Search, Images, Sparkles, Scissors, Hand } from "lucide-react";
import styles from "../styles/Gallery.module.css";

const API_BASE_URL = "https://salon-admin.deyfreshsignature.com";
const categories = [
  { value: "all", label: "All Services" },
  { value: "hair-styling", label: "Hair & Braiding" },
  { value: "wigs", label: "Wigs" },
  { value: "locs", label: "Locs" },
  { value: "barbering", label: "Barbering" },
  { value: "nails", label: "Nails" },
  { value: "pedicure", label: "Pedicure & Manicure" },
  { value: "lashes", label: "Lashes" },
  { value: "facials", label: "Facials" },
  { value: "tinting", label: "Hair Tinting" },
];

const normalizeCategory = (value) => {
  if (value === "women") return "hair-styling";
  if (value === "men") return "barbering";
  return value || "hair-styling";
};

export default function AdminGallery() {
  const [showModal, setShowModal] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({ tag: "", category: "hair-styling", image: null });

  useEffect(() => { fetchPhotos(); }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get("/api/gallery");
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Could not load gallery");
    } finally {
      setFetching(false);
    }
  };

  const filteredPhotos = useMemo(() => photos.filter((photo) => {
    const categoryMatch = activeCategory === "all" || normalizeCategory(photo.category) === activeCategory;
    const textMatch = String(photo.tag || "").toLowerCase().includes(search.toLowerCase().trim());
    return categoryMatch && textMatch;
  }), [photos, activeCategory, search]);

  const counts = useMemo(() => categories.reduce((acc, category) => {
    acc[category.value] = category.value === "all"
      ? photos.length
      : photos.filter(p => normalizeCategory(p.category) === category.value).length;
    return acc;
  }, {}), [photos]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setFormData(prev => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const closeModal = () => {
    if (loading) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setFormData({ tag: "", category: "hair-styling", image: null });
    setShowModal(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.image) return toast.error("Please select a photo");
    if (!formData.tag.trim()) return toast.error("Please enter a style name");
    setLoading(true);
    const data = new FormData();
    data.append("tag", formData.tag.trim());
    data.append("category", formData.category);
    data.append("image", formData.image);
    try {
      await axios.post("/api/admin/gallery/upload", data);
      toast.success("Photo added to gallery");
      closeModal();
      setFetching(true);
      await fetchPhotos();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await axios.delete(`/api/admin/gallery/${id}`);
      setPhotos(prev => prev.filter(p => (p._id || p.id) !== id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Could not remove photo");
    }
  };

  const categoryLabel = (value) => categories.find(c => c.value === normalizeCategory(value))?.label || value || "Gallery";

  return (
    <main className={styles.page}>
      <Toaster position="top-right" />

      <section className={styles.hero}>
        <div>
          <span className={styles.eyebrow}>PORTFOLIO MANAGEMENT</span>
          <h1>Gallery</h1>
          <p>Curate the work customers see on the Dey Fresh website.</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowModal(true)}>
          <Plus size={18} /> <span>Add Photo</span>
        </button>
      </section>

      <section className={styles.statsGrid}>
        <article className={styles.statCard}><span className={styles.statIcon}><Images size={21}/></span><div><strong>{counts.all}</strong><span>Total Photos</span></div></article>
        <article className={styles.statCard}><span className={styles.statIcon}><Sparkles size={21}/></span><div><strong>{counts["hair-styling"] + counts.wigs + counts.locs}</strong><span>Hair, Wigs & Locs</span></div></article>
        <article className={styles.statCard}><span className={styles.statIcon}><Scissors size={21}/></span><div><strong>{counts.barbering}</strong><span>Barbering</span></div></article>
        <article className={styles.statCard}><span className={styles.statIcon}><Hand size={21}/></span><div><strong>{counts.nails + counts.pedicure + counts.lashes + counts.facials}</strong><span>Beauty Services</span></div></article>
      </section>

      <section className={styles.workspace}>
        <div className={styles.workspaceHead}>
          <div><span className={styles.kicker}>YOUR WORK</span><h2>Salon Portfolio</h2><p>Keep your strongest work visible and up to date.</p></div>
          <div className={styles.searchBox}><Search size={17}/><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search gallery..." />{search && <button onClick={() => setSearch("")}><X size={15}/></button>}</div>
        </div>

        <div className={styles.filters}>
          {categories.map(cat => <button key={cat.value} className={activeCategory === cat.value ? styles.activeFilter : ""} onClick={() => setActiveCategory(cat.value)}>{cat.label}<span>{counts[cat.value] || 0}</span></button>)}
        </div>

        {fetching ? (
          <div className={styles.state}><Loader2 className={styles.spin} size={34}/><p>Loading your portfolio...</p></div>
        ) : filteredPhotos.length ? (
          <div className={styles.galleryGrid}>
            {filteredPhotos.map((photo, index) => (
              <article key={photo._id || photo.id} className={`${styles.photoCard} ${index % 7 === 0 ? styles.featured : ""}`}>
                <div className={styles.imageWrap}>
                  <img src={API_BASE_URL + photo.url} alt={photo.tag || "Dey Fresh gallery"} loading="lazy" />
                  <span className={styles.categoryBadge}>{categoryLabel(photo.category)}</span>
                  <button className={styles.deleteBtn} onClick={() => handleDelete(photo._id || photo.id)} aria-label={`Delete ${photo.tag || "photo"}`}><Trash2 size={17}/></button>
                </div>
                <div className={styles.cardInfo}><div><small>STYLE</small><h3>{photo.tag || "Untitled style"}</h3></div><span className={styles.liveBadge}>LIVE</span></div>
              </article>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}><span><CameraOff size={29}/></span><h3>{photos.length ? "No matching photos" : "Build your salon portfolio"}</h3><p>{photos.length ? "Try another category or search term." : "Upload your best hair, locs, barbering, nails, lashes, facial and beauty work for customers to discover."}</p>{!photos.length && <button onClick={() => setShowModal(true)}>Add your first photo</button>}</div>
        )}
      </section>

      {showModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <section className={styles.uploadModal}>
            <header className={styles.modalHeader}><div><span className={styles.eyebrow}>NEW GALLERY PHOTO</span><h2>Add to Portfolio</h2><p>Upload a polished image and label it so customers can find the right inspiration.</p></div><button className={styles.closeBtn} onClick={closeModal}><X size={19}/></button></header>
            <form onSubmit={handleUpload}>
              <label className={`${styles.dropzone} ${preview ? styles.hasPreview : ""}`} htmlFor="galleryFile">
                {preview ? <img src={preview} className={styles.previewImg} alt="Upload preview"/> : <div className={styles.placeholder}><span><UploadCloud size={26}/></span><strong>Choose a salon photo</strong><p>Tap to browse your device</p><small>JPG, PNG or WEBP</small></div>}
                <input type="file" id="galleryFile" hidden onChange={handleImageChange} accept="image/*" />
                {preview && <div className={styles.changePhoto}><ImageIcon size={15}/> Change photo</div>}
              </label>
              <div className={styles.formGrid}>
                <label className={styles.inputGroup}><span>Style name</span><input type="text" placeholder="e.g. Knotless Braids" value={formData.tag} onChange={e => setFormData({...formData, tag:e.target.value})}/></label>
                <label className={styles.inputGroup}><span>Service</span><select value={formData.category} onChange={e => setFormData({...formData, category:e.target.value})}>{categories.filter(c => c.value !== "all").map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></label>
              </div>
              <div className={styles.modalActions}><button type="button" className={styles.cancelBtn} onClick={closeModal}>Cancel</button><button type="submit" className={styles.submitBtn} disabled={loading}>{loading ? <><Loader2 className={styles.spin} size={17}/> Uploading...</> : <><UploadCloud size={17}/> Publish Photo</>}</button></div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
}
